import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CircleCheck, CircleX, Clock, Send, Pencil } from "lucide-react";
import Navbar, { GoogleLoginButton } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const STATUS_META = {
  none: { label: "Not Applied", cls: "bg-plum-600 text-muted-foreground border-sunset-amber/20" },
  pending: { label: "Pending Review", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  accepted: { label: "Whitelisted", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-400 border-red-500/40" },
};

export default function Profile() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [profile, setProfile] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ character_name: "", bio: "" });

  useEffect(() => {
    if (!user) {
      if (!loading) setFetching(false);
      return;
    }
    setFetching(true);
    Promise.all([
      api.get("/applications/mine").catch(() => ({ data: { application: null } })),
      api.get("/profile/me").catch(() => ({ data: { profile: null } })),
    ]).then(([appRes, profRes]) => {
      setApplication(appRes.data.application);
      setProfile(profRes.data.profile);
      if (profRes.data.profile) {
        setEditForm({
          character_name: profRes.data.profile.character_name,
          bio: profRes.data.profile.bio,
        });
      }
      setFetching(false);
    });
  }, [user, loading]);

  const status = profile ? "accepted" : application?.status || "none";
  const meta = STATUS_META[status] || STATUS_META.none;

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.put("/profile/me", editForm);
      setProfile(res.data.profile);
      setEditing(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading || (user && fetching)) {
    return (
      <div className="min-h-screen bg-plum-900 flex items-center justify-center" data-testid="profile-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sunset-amber" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-plum-900" data-testid="profile-login-prompt">
        <Navbar />
        <div className="max-w-md mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="font-display text-3xl font-extrabold uppercase text-cream mb-4">Sign in required</h1>
          <p className="text-muted-foreground text-sm mb-8">Sign in to view your profile and application status.</p>
          <GoogleLoginButton />
        </div>
      </div>
    );
  }

  const steps = [
    { key: "submitted", label: "Submitted", done: !!application, icon: Send },
    { key: "review", label: "In Review", done: status !== "none", active: status === "pending", icon: Clock },
    {
      key: "decision",
      label: status === "accepted" ? "Accepted" : status === "rejected" ? "Rejected" : "Decision",
      done: status === "accepted" || status === "rejected",
      icon: status === "rejected" ? CircleX : CircleCheck,
    },
  ];

  return (
    <div className="min-h-screen bg-plum-900" data-testid="profile-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 space-y-8">
        {/* Identity card */}
        <div className="rounded-2xl border border-sunset-amber/25 bg-plum-700/70 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <Avatar className="h-20 w-20 border-2 border-sunset-amber/50" data-testid="auth-user-avatar">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-plum-600 text-cream text-2xl">{user.name?.[0] || "U"}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-cream">{user.name}</h1>
              <Badge data-testid="auth-status-badge" className={`${meta.cls} border uppercase tracking-wider text-[10px]`}>
                {meta.label}
              </Badge>
              {user.role === "admin" && (
                <Badge className="bg-sunset-amber/15 text-sunset-amber border border-sunset-amber/50 uppercase tracking-wider text-[10px]" data-testid="admin-role-badge">
                  Staff
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
          </div>
          {status === "none" && (
            <Link to="/apply" data-testid="profile-apply-cta" className="btn-sunset rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">
              Apply Now
            </Link>
          )}
          {status === "rejected" && (
            <Link to="/apply" data-testid="profile-reapply-cta" className="btn-sunset rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">
              Re-Apply
            </Link>
          )}
        </div>

        {/* Application timeline */}
        <div className="rounded-2xl border border-sunset-amber/25 bg-plum-700/70 p-6 sm:p-8" data-testid="application-status-section">
          <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wide text-cream mb-6">
            Application <span className="sunset-text">Status</span>
          </h2>
          {!application ? (
            <p className="text-muted-foreground text-sm" data-testid="status-empty">
              You haven't applied yet. Your story starts with the whitelist application.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-0">
                {steps.map((s, i) => (
                  <div key={s.key} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center" data-testid={`status-timeline-step-${s.key}`}>
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center border ${
                          s.done
                            ? s.key === "decision" && status === "rejected"
                              ? "bg-red-500/20 border-red-500/60 text-red-400"
                              : "bg-sunset-amber/20 border-sunset-amber/60 text-sunset-amber"
                            : s.active
                              ? "bg-amber-500/10 border-amber-500/50 text-amber-400 animate-pulse"
                              : "bg-plum-600 border-sunset-amber/15 text-muted-foreground"
                        }`}
                      >
                        <s.icon className="h-5 w-5" />
                      </div>
                      <span className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`h-px flex-1 mx-2 mb-6 ${s.done ? "bg-sunset-amber/50" : "bg-sunset-amber/15"}`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Character</p>
                  <p className="text-cream font-semibold">{application.character_name}</p>
                </div>
                <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Submitted</p>
                  <p className="text-cream font-semibold">{new Date(application.created_at).toLocaleString()}</p>
                </div>
              </div>
              {application.admin_note && (
                <div className="mt-4 rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4" data-testid="admin-note-display">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Staff Note</p>
                  <p className="text-cream/90 text-sm">{application.admin_note}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Citizen profile (accepted) */}
        {profile && (
          <div className="rounded-2xl border border-sunset-amber/25 bg-plum-700/70 p-6 sm:p-8" data-testid="citizen-profile-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wide text-cream">
                Citizen <span className="sunset-text">Profile</span>
              </h2>
              <Button
                data-testid="profile-edit-button"
                variant="outline"
                onClick={() => setEditing((e) => !e)}
                className="btn-outline-amber bg-transparent rounded-full"
              >
                <Pencil className="h-4 w-4 mr-2" /> {editing ? "Cancel" : "Edit"}
              </Button>
            </div>
            {editing ? (
              <div className="space-y-5" data-testid="profile-edit-form">
                <div className="space-y-2">
                  <Label className="text-cream">Character Name</Label>
                  <Input
                    data-testid="profile-character-name-input"
                    value={editForm.character_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, character_name: e.target.value }))}
                    className="bg-plum-800 border-sunset-amber/30 text-cream focus-visible:ring-sunset-amber"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-cream">Character Bio</Label>
                  <Textarea
                    data-testid="profile-bio-input"
                    rows={6}
                    value={editForm.bio}
                    onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
                    className="bg-plum-800 border-sunset-amber/30 text-cream focus-visible:ring-sunset-amber"
                  />
                </div>
                <Button
                  data-testid="profile-save-button"
                  onClick={saveProfile}
                  disabled={saving}
                  className="btn-sunset rounded-full px-8"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Profile
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Character</p>
                    <p className="font-display text-lg font-semibold text-sunset-gold" data-testid="profile-character-name">{profile.character_name}</p>
                  </div>
                  <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Discord</p>
                    <p className="text-cream font-mono text-sm" data-testid="profile-discord-tag">{profile.discord_name}</p>
                  </div>
                  <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Citizen Since</p>
                    <p className="text-cream font-semibold">{new Date(profile.joined_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="rounded-xl bg-plum-800/80 border border-sunset-amber/15 p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Character Bio</p>
                  <p className="text-cream/90 text-sm leading-relaxed whitespace-pre-line" data-testid="profile-rp-bio">{profile.bio}</p>
                </div>
                {profile.ingame && (
                  <div className="rounded-xl bg-plum-800/80 border border-emerald-400/25 p-4" data-testid="profile-ingame-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse-dot" />
                      <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Live In-Game Stats (SQL Sync)</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div data-testid="profile-ingame-cash">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Cash</p>
                        <p className="font-mono text-sunset-gold">${profile.ingame.cash.toLocaleString()}</p>
                      </div>
                      <div data-testid="profile-ingame-bank">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Bank</p>
                        <p className="font-mono text-sunset-gold">${profile.ingame.bank.toLocaleString()}</p>
                      </div>
                      <div data-testid="profile-ingame-job">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Job</p>
                        <p className="text-cream">{profile.ingame.job}</p>
                      </div>
                      {profile.ingame.gang && (
                        <div data-testid="profile-ingame-gang">
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gang</p>
                          <p className="text-cream">{profile.ingame.gang}</p>
                        </div>
                      )}
                      <div data-testid="profile-ingame-citizenid">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Citizen ID</p>
                        <p className="font-mono text-cream/80 text-xs">{profile.ingame.citizenid}</p>
                      </div>
                      {profile.ingame.last_seen && (
                        <div data-testid="profile-ingame-last-seen">
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Last Seen</p>
                          <p className="text-cream/80">{new Date(profile.ingame.last_seen).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {user.role === "admin" && (
          <button
            data-testid="profile-admin-link"
            onClick={() => navigate("/admin")}
            className="w-full rounded-2xl border border-dashed border-sunset-amber/40 bg-plum-700/40 p-5 text-sunset-amber font-display font-bold uppercase tracking-wider hover:bg-plum-700/70 transition-colors"
          >
            Open Admin Dashboard
          </button>
        )}
      </div>
      <Footer />
    </div>
  );
}
